/**
 * The site's buttons, in class strings (design note salon-booking-organic-wellness.md section 6):
 * every button and button-like link in this app is one. There are four kinds:
 * - `accent`, the one primary action in a view (Book, Confirm, Continue), filled with the brand colour;
 * - `ink`, the green pill. With `arrow` sizing and an `ArrowDisc` (`on="ink"`) at its end it is the
 *   board's "Begin your booking" pill, the home page's lead action; it warms to the accent on hover;
 * - `outline`, the board's 1.5 px sage line, for every second action; `onInk` is the same on a green band;
 * - a text link where a button would shout: `link-line`, always underlined, and `link-draw` inside
 *   running text.
 *
 * Used on `<Button className>` (tailwind-merge lets these win over the library's own variant), or on a
 * `Link` together with `focusRing`. Every size is at least 40 px tall, and the main sizes are 48 and
 * 56. Wide pills set their label at the start.
 */
export const pill = {
  base: "inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-semibold tracking-[-0.005em] whitespace-nowrap transition-[background-color,color,box-shadow,transform] duration-400 ease-[cubic-bezier(0.3,1.2,0.5,1)] active:scale-[0.98] disabled:pointer-events-none disabled:opacity-45",
  accent: "bg-accent-500 text-on-accent hover:-translate-y-0.5 hover:bg-ink hover:text-on-ink",
  ink: "bg-ink text-on-ink hover:bg-accent-500 hover:text-on-accent",
  soft: "bg-muted-bg text-heading hover:bg-sage-tint",
  surface: "bg-surface text-heading ring-1 ring-edge-subtle ring-inset hover:bg-raised hover:ring-edge",
  outline: "bg-transparent text-heading ring-[1.5px] ring-edge ring-inset hover:bg-muted-bg",
  tint: "bg-tint text-accent-ink hover:bg-[color-mix(in_oklch,var(--color-tint)_80%,var(--color-accent-500))]",
  /** On a green band: the board's sage line and cream text, filling pale sage on hover. */
  onInk: "bg-transparent text-on-ink ring-[1.5px] ring-sage ring-inset hover:bg-sage-tint hover:text-heading",
  lg: "h-14 px-7 text-base",
  md: "h-12 px-6 text-[15px]",
  sm: "h-10 px-[1.125rem] text-sm",
  /** A pill ending in an `ArrowDisc`: the label's side is padded as usual, the disc's side tight. */
  arrow: "group h-[3.75rem] gap-3.5 pr-2 pl-[1.625rem] text-base",
  wide: "w-full justify-start",
} as const;
